export const CONTRACT_ACTIONS = {
  PAST_SHOW: 'past_agency_show',
  CURRENT_SHOW: 'current_agency_show',
  FUTURE_SHOW: 'future_agency_show',
  SHOW: 'show',
  UPDATE: 'update',
  INVITE: 'invite',
  EDIT_SCOPE: 'edit_scope',
  REMOVE: 'destroy_agency_contract',
  // Still emitted on every draft row, but the server has no route behind it. Never render it.
  UNROUTED_DESTROY: 'destroy',
} as const;

export type ContractActionKey = (typeof CONTRACT_ACTIONS)[keyof typeof CONTRACT_ACTIONS];

export const CONTRACT_TAB_ACTIONS = [
  CONTRACT_ACTIONS.PAST_SHOW,
  CONTRACT_ACTIONS.CURRENT_SHOW,
  CONTRACT_ACTIONS.FUTURE_SHOW,
] as const;

export type ContractTabActionKey = (typeof CONTRACT_TAB_ACTIONS)[number];

// How the tab's amendment is priced: a converted contract is engine-bound, but its amendments from before the
// conversion stay legacy. Anything other than legacy is read as engine.
export const CONTRACT_PRICINGS = {
  LEGACY: 'legacy',
  ENGINE: 'engine',
} as const;

export type ContractPricing = (typeof CONTRACT_PRICINGS)[keyof typeof CONTRACT_PRICINGS];

export const CONTRACT_STATUSES = {
  DRAFT: 'draft',
  INVITED: 'invited',
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  REMOVED: 'removed',
} as const;

export type ContractStatus = (typeof CONTRACT_STATUSES)[keyof typeof CONTRACT_STATUSES];

const COMBINATION_STATUSES = {
  LIVE: 'live',
  PENDING: 'pending',
  SUSPENDED: 'suspended',
} as const;

export type CombinationStatus = (typeof COMBINATION_STATUSES)[keyof typeof COMBINATION_STATUSES];

const AMENDMENT_TIMELINE_STATUSES = {
  SCHEDULED: 'scheduled',
  IN_FORCE: 'in_force',
  SUPERSEDED: 'superseded',
} as const;

export type AmendmentTimelineStatus = (typeof AMENDMENT_TIMELINE_STATUSES)[keyof typeof AMENDMENT_TIMELINE_STATUSES];

export const GATE_CODES = {
  ENGINE_NOT_BOUND: 'engine_not_bound',
  VERSION_NOT_RESOLVABLE: 'version_not_resolvable',
  CARD_SNAPSHOT_MISSING: 'card_snapshot_missing',
  CARD_SIDE_MISSING: 'card_side_missing',
  SCOPE_EMPTY: 'scope_empty',
  COVERAGE_GAPS: 'coverage_gaps',
} as const;

export type GateCode = (typeof GATE_CODES)[keyof typeof GATE_CODES];

export const GATE_FAILED_CODE = 'gate_failed';

export const RATE_LINE_SIDES = {
  PAY: 'pay',
  BILL: 'bill',
} as const;

export type RateLineSide = (typeof RATE_LINE_SIDES)[keyof typeof RATE_LINE_SIDES];

export const RATE_LINE_SIDE_VALUES = [RATE_LINE_SIDES.PAY, RATE_LINE_SIDES.BILL] as const;

const UNRESOLVED_REASONS = {
  NO_MATCHING_LINE: 'no_matching_line',
  NO_ACTIVE_CARD: 'no_active_card',
  VERSION_NOT_RESOLVABLE: 'version_not_resolvable',
} as const;

export type UnresolvedReason = (typeof UNRESOLVED_REASONS)[keyof typeof UNRESOLVED_REASONS];

export const COVERAGE_STATES = {
  FULLY_COVERED: 'fully_covered',
  SUSPENDED: 'suspended',
  AWAITING_RATES: 'awaiting_rates',
  NOT_APPLICABLE: 'not_applicable',
} as const;

export type CoverageState = (typeof COVERAGE_STATES)[keyof typeof COVERAGE_STATES];

export const AGENCY_MODES = {
  EXISTING: 'existing',
  NEW: 'new',
} as const;

export type AgencyMode = (typeof AGENCY_MODES)[keyof typeof AGENCY_MODES];

export const CONTRACT_EDITOR_MODES = {
  CREATE: 'create',
  EDIT: 'edit',
  SCOPE: 'scope',
} as const;

export type ContractEditorMode = (typeof CONTRACT_EDITOR_MODES)[keyof typeof CONTRACT_EDITOR_MODES];

export const CONTRACT_PICKER_PAGE_SIZE = 25;
// The server's page-size ceiling; anything larger is clamped. The engine list is read whole.
export const CONTRACT_PICKER_FULL_PAGE_SIZE = 100;
export const CONTRACT_PICKER_MAX_PAGES = 100;
// Room for the header and ten 64px body rows, rounded up: ten rows always visible outranks a pane that never
// scrolls, so on an ordinary laptop this floor is what the grid gets. Also the height until the first measure.
export const SCOPE_TABLE_MIN_HEIGHT = 680;
// What follows the scroller in the pane: the step card's padding, the editor footer and its gap, the page padding
// and the app footer — summed from their styles with a little slack, not measured in a browser.
export const SCOPE_TABLE_BOTTOM_RESERVE = 232;

export const RATE_LINES_PAGE_SIZE = 25;
export const RATE_LINES_MAX_HEIGHT = 520;

// One pause length for every debounced request: the pickers' searches, the address lookup and the priced-set
// reload after a tick.
export const TYPING_DEBOUNCE_MS = 400;

// Matches the Rate Engine and Rate Cards editors, which both sit at 864, rather than setting a third width.
export const CONTRACT_EDITOR_PAGE_MAX_WIDTH = 864;

// Each is a section's `headerKey`, and therefore also a value the `?tab[]=` query carries.
export const CONTRACT_VIEW_SECTION_KEYS = [
  'agencyContractDetails',
  'agencyContractPricing',
  'agencyContractScope',
  'agencyContractRateLines',
  'cancellation-policy',
  'users',
  'logs',
] as const;

export type ContractViewSectionKey = (typeof CONTRACT_VIEW_SECTION_KEYS)[number];

export const CONTRACT_VIEW_OPEN_ON_ARRIVAL: readonly ContractViewSectionKey[] = ['agencyContractDetails'];
